/**
 * @graphql cc
 */
'use no memo';
import { graphql } from 'react-relay';
import type { FC } from 'react';
import React, { useCallback } from 'react';
import { styled, css } from '@compiled/react';
import { useIntl, defineMessages } from 'react-intl';

import Avatar from '@atlaskit/avatar/avatar';
import type { SizeType } from '@atlaskit/avatar/types';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Text from '@atlaskit/primitives/text';
import Inline from '@atlaskit/primitives/inline';
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import PageIcon from '@atlaskit/icon/core/page';

import { ProfileLink } from '@confluence/profile/entry-points/ProfileLinkComponent.ts';
import { ProfileAvatar } from '@confluence/profile/entry-points/ProfileAvatarComponent.ts';
import { TimeAgo, formatTimeAgo } from '@confluence/time-ago';
import { ExternalCollaboratorUserLozenge } from '@confluence/external-collab-ui/entry-points/ExternalUserLozengeLoader.ts';
import { getValidDate } from '@confluence/comments-util/entry-points/dateUtils.ts';
import type { FooterResolvedProperties } from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery.ts';
import { SitePermissionTypeValues } from '@confluence/inline-comments-queries/entry-points/InlineCommentsQuery.ts';
import type { InlineCommentAuthorQuery_content_nodes_history_createdBy_KnownUser } from '@confluence/inline-comments-queries/entry-points/InlineCommentAuthorQuery.ts';
import type { CommentInlineResolveProperties } from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.ts';
import type { SitePermissionType } from '@confluence/inline-comments-queries/entry-points/InlineCommentQuery.ts';
import { fg } from '@confluence/feature-gating';
import { commentsi18n } from '@confluence/comments-util/entry-points/commentsi18n.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { RelativeTimestamp } from '@confluence/inline-comments-utils/entry-points/RelativeTimestamp.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

import type {
	CommentAuthorInlineCommentAuthorQuery as CommentAuthorInlineCommentAuthorQuery$data,
	CommentAuthorInlineCommentAuthorQueryVariables as CommentAuthorInlineCommentAuthorQuery$variables,
} from './__types__/CommentAuthorInlineCommentAuthorQuery.ts';
import type {
	CommentAuthorUserQuery as CommentAuthorUserQuery$data,
	CommentAuthorUserQueryVariables as CommentAuthorUserQuery$variables,
} from './__types__/CommentAuthorUserQuery.ts';
import type { CommentContributor, CommentMode } from './inlineCommentsTypes.ts';
import { CopyLinkButton } from './CopyLinkButton.tsx';
import { MaybeTooltip } from './MaybeTooltip.tsx';
import { useCommentAuthorQuery } from './useCommentAuthorQuery.ts';
const CommentAuthorInlineCommentAuthorQuery = graphql`
	query CommentAuthorInlineCommentAuthorQuery($commentId: ID!) {
		content(id: $commentId) {
			nodes {
				id
				history {
					createdBy {
						displayName
						profilePicture {
							path
						}
						... on KnownUser {
							accountId
						}
					}
				}
			}
		}
	}
`;
const CommentAuthorUserQuery = graphql`
	query CommentAuthorUserQuery($id: ID!) {
		user: confluence_atlassianUser(id: $id) {
			id
			displayName
			photos {
				value
			}
		}
	}
`;

type CommentAuthorProps = {
	commentMode: CommentMode;
	userId?: string | null;
	avatarUrl?: string | null;
	displayName?: string | null;
	date?: string | null;
	createdAtNonLocalized?: string | null;
	versionByDisplayName?: string | null;
	versionDate?: string | null;
	size?: SizeType;
	commentDateUrl?: string;
	commentId?: string | null;
	permissionType?: SitePermissionType;
	isInactiveComment?: boolean;
	showCopyLink?: boolean;
	resolveProperties?: CommentInlineResolveProperties | FooterResolvedProperties;
	isCommentsPanel?: boolean;
	isGeneralComments?: boolean;
	isReply?: boolean;
	isEdited?: boolean;
	isFooterComment?: boolean;
	isStandaloneComment?: boolean;
	contributors?: ReadonlyArray<CommentContributor> | null;
};

type User = InlineCommentAuthorQuery_content_nodes_history_createdBy_KnownUser;

type AuthorContainerProps = {
	commentMode: string;
	isStandaloneComment?: boolean;
	isFooterComment?: boolean;
	isReply?: boolean;
	isFixedEditStyles: boolean;
};

type DateContainerProps = {
	isStandaloneComment: boolean;
	isCommentsPanel?: boolean;
	isFixedEditStyles?: boolean;
};

const getAuthorContainerMarginLeft = ({
	isFixedEditStyles,
	commentMode,
	isReply,
	isStandaloneComment,
	isFooterComment,
}: AuthorContainerProps) => {
	if (isFixedEditStyles) {
		const negativeMarginForEditMode = expValEquals('confluence_comments_tuneup', 'isEnabled', true)
			? '-16px'
			: '-24px';

		return commentMode === 'edit'
			? isReply || isStandaloneComment || isFooterComment
				? '0'
				: negativeMarginForEditMode
			: undefined;
	}
	return commentMode === 'reply' ? '-2px' : 'inherit';
};

const getAuthorContainerPaddingBottom = ({
	isFixedEditStyles,
	commentMode,
	isReply,
}: AuthorContainerProps) => {
	if (isFixedEditStyles) {
		return commentMode === 'edit' ? (isReply ? '8px' : '12px') : undefined;
	}
	return commentMode === 'view' ? undefined : '8px';
};

const authorContainerBaseStyles = css({
	display: 'flex',
	alignItems: 'center',
	minWidth: 0,
	overflow: 'hidden',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> a': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		color: `${token('color.text')} !important`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'&:visited, &:hover': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			color: `${token('color.text')} !important`,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			'text-decoration': 'none !important',
		},
	},
});
const AuthorContainer: FC<
	AuthorContainerProps & {
		children: React.ReactNode;
	}
> = (props) => {
	const {
		commentMode: _commentMode,
		isReply: _isReply,
		isStandaloneComment: _isStandaloneComment,
		isFooterComment: _isFooterComment,
		isFixedEditStyles: _isFixedEditStyles,
		...rest
	} = props;
	return (
		<div
			css={authorContainerBaseStyles}
			style={{
				marginLeft: getAuthorContainerMarginLeft(props),
				paddingBottom: getAuthorContainerPaddingBottom(props),
			}}
			{...rest}
		/>
	);
};

const avatarWrapperBaseStyles = css({
	display: 'flex',
	position: 'relative',
});

const avatarSmallPaddingStyles = css({
	paddingRight: token('space.075'),
});

const avatarLargePaddingStyles = css({
	paddingRight: token('space.100'),
});

const AvatarWrapper: FC<{
	isCommentsTuneupActive: boolean;
	isCommentsPanel: boolean;
	isFooterComment: boolean;
	isFixedEditStyles: boolean;
	children: React.ReactNode;
}> = ({
	isCommentsTuneupActive,
	isCommentsPanel,
	isFooterComment,
	isFixedEditStyles,
	...props
}) => {
	return (
		<div
			css={[
				avatarWrapperBaseStyles,
				!isCommentsTuneupActive && (isCommentsPanel || isFooterComment || isFixedEditStyles)
					? avatarSmallPaddingStyles
					: avatarLargePaddingStyles,
			]}
			{...props}
		/>
	);
};

const containerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	minWidth: 0,
	flex: '1 1 0%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> a': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		color: `${token('color.text')} !important`,
		font: token('font.body'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'&:visited, &:hover': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
			color: `${token('color.text')} !important`,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'&:focus-visible': {
			outline: `2px solid ${token('color.border.focused')}`,
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span.comment-timestamp': {
		font: token('font.body.small'),
		color: token('color.text.subtle'),
		margin: 0,
		'background-color': 'transparent',
		border: 'none',

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> a': {
			color: token('color.text.subtle'),
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'&:visited, &:hover': {
				color: token('color.text.subtle'),
			},
		},
	},
});

const Container: FC<{
	children: React.ReactNode;
	useProfileLinkStyle?: boolean;
	useColumnStyle?: boolean;
}> = ({ useProfileLinkStyle, useColumnStyle, ...props }) => {
	return (
		<div
			css={[
				containerStyles,
				useProfileLinkStyle && profileLinkContainerStyle,
				useColumnStyle && profileLinkContainerColumnStyle,
			]}
			{...props}
		/>
	);
};

const newPageIconStyle = xcss({
	position: 'relative',
});

// TODO: remove the following 3 styles when cleaning up `confluence_frontend_content_wrapper` flag
const resolvedPropertiesStyle = xcss({
	paddingLeft: 'space.050',
});

const pageIconStyle = xcss({
	position: 'relative',
	top: 'space.negative.025',
});

const coAuthorPersonAvatarStyle = xcss({
	marginLeft: 'space.250',
	paddingLeft: 'space.025',
});

const coAuthorBotAvatarStyle = xcss({
	position: 'absolute',
	top: 'space.0',
	left: 'space.0',
});

const coAuthorCont = xcss({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	gap: 'space.075',
});

const generalCommentFooterStyle = xcss({
	top: 'space.negative.050',
});

const profileLinkContainerStyle = css({
	flexDirection: 'row',
	alignItems: 'baseline',
	gap: token('space.100'),
	minWidth: 0,
	overflow: 'hidden',
	flex: '1 1 0%',
});

const profileLinkContainerColumnStyle = css({
	flexDirection: 'column',
	alignItems: 'flex-start',
	gap: 0,
});

const truncateTextStyle = {
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	minWidth: 0,
	flex: '1 1 0%',
	wordBreak: 'unset',
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProfileLinkContainer = styled(Container)({
	flexDirection: 'row',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LozengeWrapper = styled.span({
	paddingLeft: `${token('space.100')}`,
	verticalAlign: 'top',
});

const dateContainerBaseStyles = css({
	display: 'flex',
	flexFlow: 'row nowrap',
	flexDirection: 'unset',
	alignItems: 'unset',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span': {
		font: token('font.body.small'),
		margin: 0,
		backgroundColor: 'transparent',
		border: 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> a': {
			color: token('color.text.subtle'),
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'&:visited, &:hover': {
				color: token('color.text.subtle'),
			},
		},
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div button:focus': {
		boxShadow: `0 0 0 2px ${token('color.border.focused')}`,
	},
});

const dateContainerStandaloneStyles = css({
	flexDirection: 'column',
	alignItems: 'flex-start',
});

const dateContainerSubtlestStyles = css({
	color: token('color.text.subtlest'),
});
const DateContainer: FC<
	DateContainerProps & {
		children: React.ReactNode;
	}
> = ({ isStandaloneComment, isCommentsPanel, isFixedEditStyles, ...props }) => {
	return (
		<div
			css={[
				dateContainerBaseStyles,
				isStandaloneComment &&
					fg('confluence_frontend_content_wrapper') &&
					dateContainerStandaloneStyles,
				(isStandaloneComment || (isCommentsPanel && isFixedEditStyles)) &&
					dateContainerSubtlestStyles,
			]}
			{...props}
		/>
	);
};

// confluence_comments_cohiti_date_tooltips exp evaluations will use this component-level i18n object instead of commentsI18n
const i18n = defineMessages({
	defaultTooltip: {
		id: 'inline-comments-common.date-tooltip.default',
		defaultMessage: 'Added on {date} at {timestamp}',
		description: 'Tooltip to provide relative temporal context for when comment was added',
	},
	addedTooltip: {
		id: 'inline-comments-common.date-tooltip.added',
		defaultMessage: 'Added by {username} on {date} at {timeStamp}',
		description:
			'Tooltip to provide name of author and relative temporal context for when comment was added',
	},
	resolvedTooltip: {
		id: 'inline-comments-common.date-tooltip.resolved',
		defaultMessage: 'Resolved by {username} on {date} at {timeStamp}',
		description:
			'Tooltip to provide name of resolver and relative temporal context for when comment was resolved',
	},
	reopenedTooltip: {
		id: 'inline-comments-common.date-tooltip.reopened',
		defaultMessage: 'Reopened by {username} on {date} at {timeStamp}',
		description:
			'Tooltip to provide name of re-opening user and relative temporal context for when comment was re-opened',
	},
	reopenedDateInfoText: {
		id: 'inline-comments-common.reopened.date.info.text',
		defaultMessage: `Reopened {timeAgo}{timeStamp, select,
			undefined {}
			other { at {timeStamp}}
		}`,
		description:
			'Info text on reopened comments describing when the comment was reopened, omitting timestamp if less than 24 hours have passed. For example: "Reopened May 3, 2024 at 1:04 PM" or "Reopened 5 minutes ago"',
	},
	resolvedDateInfoText: {
		id: 'inline-comments-common.resolved.date.info.text',
		defaultMessage: 'Resolved {timeAgo}',
		description:
			'Info text on resolved comments describing when the comment was resolved, for example: Resolved May 3, 2024',
	},
	coAuthorWithUser: {
		id: 'inline-comments-common.co-author.with-user',
		defaultMessage: 'with {username}',
		description:
			'Text shown next to the co-author name indicating who the human co-author is, for example: "with John Smith"',
	},
});

export const CommentAuthor: FC<CommentAuthorProps> = ({
	commentMode,
	userId,
	date,
	createdAtNonLocalized,
	size,
	displayName,
	avatarUrl,
	commentDateUrl,
	commentId,
	permissionType,
	isInactiveComment,
	showCopyLink = false,
	resolveProperties,
	isCommentsPanel = false,
	isGeneralComments = false,
	isReply = false,
	isFooterComment = false,
	isStandaloneComment = false,
	contributors,
}) => {
	const renderServerPlaceholder = useRenderServerPlaceholder();
	const skipUserIdPath = Boolean((avatarUrl && displayName) || !userId || commentId);
	const coAuthor = contributors?.find((contributor) => contributor?.displayName);
	const coAuthorName = coAuthor?.displayName;
	const coAuthorAvatarUrl = coAuthor?.profilePicture?.path;
	const shouldShowCoAuthor = Boolean(coAuthorName && !renderServerPlaceholder);
	const { data: authorData } = useQueryInterop<
		CommentAuthorInlineCommentAuthorQuery$data,
		CommentAuthorInlineCommentAuthorQuery$variables
	>(CommentAuthorInlineCommentAuthorQuery, {
		variables: { commentId: commentId! },
		fetchPolicy: 'cache-and-network',
		skip: Boolean(userId || !commentId),
	});
	const { data: userDataFromOld } = useQueryInterop<
		CommentAuthorUserQuery$data,
		CommentAuthorUserQuery$variables
	>(CommentAuthorUserQuery, {
		variables: { id: userId! },
		fetchPolicy: 'cache-and-network',
		skip:
			skipUserIdPath || expValEquals('confluence_comment_improve_author_paint', 'isEnabled', true),
	});
	const { data: userDataFromNew } = useCommentAuthorQuery(userId ?? undefined, {
		skip:
			skipUserIdPath || !expValEquals('confluence_comment_improve_author_paint', 'isEnabled', true),
		fetchPolicy: 'cache-and-network',
	});
	const userData = expValEquals('confluence_comment_improve_author_paint', 'isEnabled', true)
		? userDataFromNew
		: userDataFromOld;

	const author = (authorData?.content?.nodes || [])[0]?.history?.createdBy;
	const userAvatar = (
		author ? author.profilePicture?.path : (userData?.user?.photos || [])[0]?.value
	) as string | undefined;
	const userDisplayName = author ? author.displayName : userData?.user?.displayName;
	const userAccountId = (author as User)?.accountId;

	const linkToComment = `${window.location.origin}/wiki${commentDateUrl}`;
	const intl = useIntl();
	const { formatMessage } = intl;
	const { timeZone: userTimeZone, tenantTimeZone } = useSessionData();
	const timeZone = userTimeZone || tenantTimeZone || undefined;

	const getTooltipContent = () => {
		if (
			resolveProperties?.resolvedTime &&
			expValEquals('confluence_comments_cohiti_date_tooltips', 'isEnabled', true)
		) {
			// resolveProperties.resolved = true -> comment is in resolved state
			// resolveProperties.resolved = false -> comment is in re-opened state
			const i18nMessage = resolveProperties?.resolved ? i18n.resolvedTooltip : i18n.reopenedTooltip;
			const resolvedTimeBase = new Date(resolveProperties.resolvedTime);

			return formatMessage(i18nMessage, {
				username: resolveProperties?.resolvedUser,
				date: intl.formatDate(resolvedTimeBase, {
					month: 'short',
					day: 'numeric',
					year: 'numeric',
					timeZone,
				}),
				timeStamp: intl.formatTime(resolvedTimeBase, {
					hour: 'numeric',
					minute: 'numeric',
					timeZone,
				}),
			});
		}

		if (resolveProperties?.resolved) {
			return formatMessage(commentsi18n.resolvedByTooltipTextCommentsPanel, {
				username: resolveProperties?.resolvedUser,
			});
		}
		return formatMessage(commentsi18n.reopenedByTooltipTextCommentsPanel, {
			username: resolveProperties?.resolvedUser,
			createdAtNonLocalized: <TimeAgo date={getValidDate(createdAtNonLocalized)} usePrepositions />,
		});
	};

	const oldStandloneExperienceResolveInfo = () => {
		return (
			<Tooltip content={resolveProperties?.resolvedFriendlyDate}>
				<span
					aria-label={formatMessage(commentsi18n.resolvedByInfoText, {
						username: resolveProperties?.resolvedUser,
						friendlyDate: resolveProperties?.resolvedFriendlyDate,
					})}
				>
					<Text color="color.text.subtlest" size="small" maxLines={2}>
						{formatMessage(commentsi18n.resolvedByInfoText, {
							username: resolveProperties?.resolvedUser,
							friendlyDate: resolveProperties?.resolvedFriendlyDate,
						})}
					</Text>
				</span>
			</Tooltip>
		);
	};

	const getResolvedDateInfoText = () => {
		// resolveProperties.resolved = true -> comment is in resolved state
		// resolveProperties.resolved = false -> comment is in re-opened state
		const i18nKey = resolveProperties?.resolved ? 'resolvedDateInfoText' : 'reopenedDateInfoText';

		if (
			resolveProperties?.resolvedTime &&
			expValEquals('confluence_comments_cohiti_date_tooltips', 'isEnabled', true)
		) {
			const resolvedTimeBase = new Date(resolveProperties.resolvedTime);
			const twentyFourHourThreshold = 24 * 60 * 60; // in seconds
			const secondsSinceReopen = (Date.now() - resolvedTimeBase.getTime()) / 1000;
			// Don't include a timestamp if the comment was re-opened less than 24 hours ago
			const shouldIncludeTimeStamp = secondsSinceReopen >= twentyFourHourThreshold;

			return formatMessage(i18n[i18nKey], {
				// use formatTimeAgo directly so that this string can be injected into aria-label,
				timeAgo: formatTimeAgo(intl, resolvedTimeBase, true, {}, userTimeZone, tenantTimeZone),
				timeStamp:
					shouldIncludeTimeStamp && !expValEquals('confluence_comments_tuneup', 'isEnabled', true)
						? intl.formatTime(resolvedTimeBase, {
								hour: 'numeric',
								minute: 'numeric',
								timeZone,
							})
						: undefined,
			});
		} else {
			return formatMessage(commentsi18n[i18nKey], {
				friendlyDate: resolveProperties?.resolvedFriendlyDate,
			});
		}
	};

	const newExperienceResolveInfo = () => {
		return (
			<Tooltip position="top" content={getTooltipContent()}>
				<span aria-label={getResolvedDateInfoText()}>
					<Text color="color.text.subtlest" size="small" maxLines={2}>
						{getResolvedDateInfoText()}
					</Text>
				</span>
			</Tooltip>
		);
	};

	const renderResolveInfo = () => {
		const standAloneExperience = !isFooterComment && !isCommentsPanel;
		if (
			(standAloneExperience && fg('confluence_frontend_content_wrapper')) ||
			(isFooterComment && fg('confluence_frontend_content_wrapper')) ||
			isCommentsPanel
		) {
			return newExperienceResolveInfo();
		}
		return oldStandloneExperienceResolveInfo();
	};

	const hasPageCommentIconEnabled = useCallback(() => {
		if (!isGeneralComments) {
			return false;
		}

		return !isFooterComment;
	}, [isGeneralComments, isFooterComment]);

	// TODO: remove renderDateInfoOldExperience() when cleaning up `confluence_frontend_content_wrapper` flag
	const renderDateInfoOldExperience = (resolvedInfo: React.ReactNode) => {
		return resolveProperties?.resolved || resolveProperties?.resolvedFriendlyDate ? (
			<>
				{resolvedInfo}
				{hasPageCommentIconEnabled() && !isReply && fg('confluence_frontend_content_wrapper') && (
					<Box
						xcss={[
							pageIconStyle,
							resolvedPropertiesStyle,
							isFooterComment && generalCommentFooterStyle,
						]}
					>
						<Tooltip content={formatMessage(commentsi18n.generalCommentTooltip)}>
							<PageIcon
								color={token('color.icon.subtlest')}
								testId="general-comment-page-icon"
								label={formatMessage(commentsi18n.generalCommentTooltip)}
							/>
						</Tooltip>
					</Box>
				)}
			</>
		) : (
			<span>
				<Inline space="space.050">
					<TimeAgo
						date={getValidDate(
							fg('confluence_frontend_content_wrapper') ? createdAtNonLocalized : date,
						)}
					/>
					{hasPageCommentIconEnabled() && !isReply && fg('confluence_frontend_content_wrapper') && (
						<Box xcss={pageIconStyle}>
							<Tooltip content={formatMessage(commentsi18n.generalCommentTooltip)}>
								<PageIcon
									color={token('color.icon.subtlest')}
									testId="general-comment-page-icon"
									label={formatMessage(commentsi18n.generalCommentTooltip)}
								/>
							</Tooltip>
						</Box>
					)}
				</Inline>
			</span>
		);
	};

	const renderCopyButton = () => {
		const resolvedInfo = renderResolveInfo();

		const renderCopyLinkButton =
			commentDateUrl && !fg('confluence_frontend_content_wrapper') && !isCommentsPanel;

		const timeAgoDate = fg('confluence_frontend_content_wrapper') ? createdAtNonLocalized : date;

		return (
			<DateContainer
				isStandaloneComment={isStandaloneComment}
				isCommentsPanel={isCommentsPanel}
				isFixedEditStyles={fg('confluence_frontend_content_wrapper')}
			>
				{fg('confluence_frontend_content_wrapper') ? (
					<span>
						<Inline space="space.050">
							{/* show resolve info if it exists, or creation date otherwise */}
							{resolveProperties?.resolved || resolveProperties?.resolvedFriendlyDate ? (
								resolvedInfo
							) : (
								<Tooltip
									content={
										expValEquals('confluence_comments_cohiti_date_tooltips', 'isEnabled', true)
											? formatMessage(i18n.defaultTooltip, {
													date: intl.formatDate(getValidDate(timeAgoDate), {
														month: 'short',
														day: 'numeric',
														year: 'numeric',
														timeZone,
													}),
													timestamp: intl.formatTime(getValidDate(timeAgoDate), {
														hour: 'numeric',
														minute: 'numeric',
														timeZone,
													}),
												})
											: undefined
									}
								>
									{expValEquals('confluence_comments_tuneup', 'isEnabled', true) ? (
										<RelativeTimestamp date={getValidDate(timeAgoDate)} />
									) : (
										<TimeAgo date={getValidDate(timeAgoDate)} />
									)}
								</Tooltip>
							)}
							{hasPageCommentIconEnabled() &&
								!isReply &&
								fg('confluence_frontend_content_wrapper') && (
									<Box xcss={newPageIconStyle}>
										<Tooltip content={formatMessage(commentsi18n.generalCommentTooltip)}>
											<PageIcon
												color={token('color.icon.subtlest')}
												testId="general-comment-page-icon"
												label={formatMessage(commentsi18n.generalCommentTooltip)}
											/>
										</Tooltip>
									</Box>
								)}
						</Inline>
					</span>
				) : (
					renderDateInfoOldExperience(resolvedInfo)
				)}
				{renderCopyLinkButton && (
					<CopyLinkButton visible={showCopyLink} textToCopy={linkToComment} />
				)}
			</DateContainer>
		);
	};

	const isDateShown = (commentMode === 'view' || fg('confluence_frontend_content_wrapper')) && date;
	const humanAuthorName = displayName || userDisplayName;
	const name = humanAuthorName || '';
	const shouldShowHumanAuthorByline = Boolean(shouldShowCoAuthor && humanAuthorName);

	return (
		<AuthorContainer
			commentMode={commentMode}
			isReply={isReply}
			isStandaloneComment={isStandaloneComment}
			isFooterComment={isFooterComment}
			isFixedEditStyles={fg('confluence_frontend_content_wrapper')}
			data-testid="comment-author"
		>
			<AvatarWrapper
				isCommentsPanel={isCommentsPanel}
				isFooterComment={isFooterComment}
				isFixedEditStyles={fg('confluence_frontend_content_wrapper')}
				isCommentsTuneupActive={expValEquals('confluence_comments_tuneup', 'isEnabled', true)}
			>
				<Box xcss={shouldShowCoAuthor ? coAuthorPersonAvatarStyle : undefined}>
					<ProfileAvatar
						isBlockLevel={expValEquals('confluence_comments_tuneup', 'isEnabled', true)}
						userId={userId}
						src={avatarUrl || userAvatar}
						trigger="click"
						size={size}
						name={name}
					/>
				</Box>
				{shouldShowCoAuthor && (
					<Box xcss={coAuthorBotAvatarStyle}>
						<Avatar src={coAuthorAvatarUrl ?? undefined} size={size} name={coAuthorName ?? ''} />
					</Box>
				)}
			</AvatarWrapper>
			<MaybeTooltip date={date} name={name}>
				<Container
					useProfileLinkStyle={expValEquals('confluence_comments_tuneup', 'isEnabled', true)}
					useColumnStyle={
						!!(resolveProperties?.resolved || resolveProperties?.resolvedFriendlyDate) &&
						expValEquals('confluence_comments_tuneup', 'isEnabled', true)
					}
				>
					<ProfileLinkContainer>
						{/* When co-author (agent) present, show agent name as primary — no profile link since agents have no profile page */}
						{isInactiveComment ? (
							<span data-testid="dummy-profile-link">{humanAuthorName}</span>
						) : shouldShowCoAuthor ? (
							<Box xcss={coAuthorCont}>
								<Text weight="medium">{coAuthorName}</Text>
							</Box>
						) : (
							<ProfileLink
								fullName={humanAuthorName!}
								userId={userId || userAccountId}
								style={{
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
									wordBreak: 'break-word',
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
									color: token('color.text'),
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
									fontWeight: token('font.weight.medium'),
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
									...(expValEquals('confluence_comments_tuneup', 'isEnabled', true) &&
										truncateTextStyle),
								}}
								isUnderlinedByDefault={false}
							/>
						)}
						{shouldShowHumanAuthorByline && (
							<Text color="color.text.subtlest" size="small">
								{formatMessage(i18n.coAuthorWithUser, {
									username: humanAuthorName,
								})}
							</Text>
						)}
						{permissionType === SitePermissionTypeValues.EXTERNAL ? (
							<LozengeWrapper>
								<ExternalCollaboratorUserLozenge />
							</LozengeWrapper>
						) : null}
					</ProfileLinkContainer>
					{isDateShown && renderCopyButton()}
				</Container>
			</MaybeTooltip>
		</AuthorContainer>
	);
};
